/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useDebounce } from 'src/core/hooks/useDebounce';
import { useDelayedEffect } from 'src/core/hooks/useDelayedEffect';

export type WithDebouncedInputBaseProps = {
    onChange?: (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => void;
    value?: string;
};

export type WithDebouncedInputProps = {
    /**
     * The time to wait before emitting the local state
     * change to the parent element.
     */
    debounceTime?: number;
};

/**
 * A higher order component with that wraps Input fields,
 * and does not sync its local value with the parent container
 * until after a specified debounce time value.
 */
export const withDebouncedInput = <TProps extends WithDebouncedInputBaseProps>(Component: React.ComponentType<TProps>) => (
    props: TProps & WithDebouncedInputProps
) => {
    const { value, onChange, debounceTime = 700 } = props;
    const [localQuery, setLocalQuery] = React.useState(value);
    const debouncedQuery = useDebounce<string>(localQuery, debounceTime);

    /**
     * Keeps the value from the parent in sync with the
     * local value to maintain "controlled" state.
     */
    React.useEffect(() => {
        setLocalQuery(value);
    }, [value]);

    /**
     * Ensures that the consumer is updated with
     * any change in the debounced query.
     */
    useDelayedEffect(() => {
        if (onChange) {
            onChange(undefined, debouncedQuery);
        }
    }, [debouncedQuery]);

    /**
     * Handle change in the wrapped input.
     */
    const handleChange = React.useCallback((_: React.ChangeEvent<HTMLInputElement>, newLocalQuery: string) => {
        setLocalQuery(newLocalQuery ?? '');
    }, []);

    return <Component {...props} value={localQuery} onChange={handleChange} />;
};
